import { supabase } from './supabase';
import type { Exercise, ExerciseSearchOptions, ExerciseSearchResult } from '../types';

// Mock exercises data for development mode with Russian schema
const MOCK_EXERCISES: Exercise[] = [
  {
    id: 'mock-1',
    name_en: 'Bench Press',
    name_ru: 'Жим лёжа',
    description: 'Базовое упражнение для развития грудных мышц',
    primary_muscles: ['грудь'],
    secondary_muscles: ['трицепс', 'передние дельты'],
    equipment: ['штанга', 'скамья'],
    tags: ['базовое', 'толкающее', 'сила'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'средний'
  },
  {
    id: 'mock-2',
    name_en: 'Squat',
    name_ru: 'Приседания',
    description: 'Базовое упражнение для развития мышц ног',
    primary_muscles: ['квадрицепс', 'ягодицы'],
    secondary_muscles: ['бицепс бедра', 'икры'],
    equipment: ['штанга'],
    tags: ['базовое', 'ноги', 'сила'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'средний'
  },
  {
    id: 'mock-3',
    name_en: 'Deadlift',
    name_ru: 'Становая тяга',
    description: 'Базовое упражнение для развития мышц спины и ног',
    primary_muscles: ['спина', 'ягодицы'],
    secondary_muscles: ['бицепс бедра', 'трапеции'],
    equipment: ['штанга'],
    tags: ['базовое', 'тяговое', 'сила'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'продвинутый'
  },
  {
    id: 'mock-4',
    name_en: 'Pull-ups',
    name_ru: 'Подтягивания',
    description: 'Упражнение с собственным весом для развития спины',
    primary_muscles: ['широчайшие'],
    secondary_muscles: ['бицепс', 'задние дельты'],
    equipment: ['турник'],
    tags: ['собственный вес', 'тяговое', 'калистеника'],
    exercise_type: 'собственный_вес_повторения',
    difficulty_level: 'средний'
  },
  {
    id: 'mock-5',
    name_en: 'Push-ups',
    name_ru: 'Отжимания',
    description: 'Упражнение с собственным весом для развития груди',
    primary_muscles: ['грудь'],
    secondary_muscles: ['трицепс', 'передние дельты'],
    equipment: ['собственный вес'],
    tags: ['собственный вес', 'толкающее', 'калистеника'],
    exercise_type: 'собственный_вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-6',
    name_en: 'Dumbbell Curl',
    name_ru: 'Подъём гантелей на бицепс',
    description: 'Изолирующее упражнение для развития бицепса',
    primary_muscles: ['бицепс'],
    secondary_muscles: ['предплечья'],
    equipment: ['гантели'],
    tags: ['изолирующее', 'руки'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-7',
    name_en: 'Shoulder Press',
    name_ru: 'Жим плечами',
    description: 'Базовое упражнение для развития плеч',
    primary_muscles: ['передние дельты'],
    secondary_muscles: ['трицепс', 'верх груди'],
    equipment: ['гантели'],
    tags: ['базовое', 'толкающее', 'плечи'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'средний'
  },
  {
    id: 'mock-8',
    name_en: 'Plank',
    name_ru: 'Планка',
    description: 'Статическое упражнение для укрепления кора',
    primary_muscles: ['кор'],
    secondary_muscles: ['плечи', 'ягодицы'],
    equipment: ['собственный вес'],
    tags: ['статическое', 'кор', 'выносливость'],
    exercise_type: 'на_время',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-9',
    name_en: 'Lunges',
    name_ru: 'Выпады',
    description: 'Функциональное упражнение для развития ног',
    primary_muscles: ['квадрицепс', 'ягодицы'],
    secondary_muscles: ['бицепс бедра', 'икры'],
    equipment: ['собственный вес'],
    tags: ['функциональное', 'ноги', 'баланс'],
    exercise_type: 'собственный_вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-10',
    name_en: 'Dips',
    name_ru: 'Отжимания на брусьях',
    description: 'Упражнение с собственным весом для груди и трицепса',
    primary_muscles: ['низ груди'],
    secondary_muscles: ['трицепс', 'передние дельты'],
    equipment: ['брусья'],
    tags: ['собственный вес', 'толкающее', 'калистеника'],
    exercise_type: 'собственный_вес_повторения',
    difficulty_level: 'средний'
  },
  // Упражнения на спину
  {
    id: 'mock-11',
    name_en: 'Wide Grip Lat Pulldown',
    name_ru: 'Вертикальная тяга широким прямым хватом к груди',
    description: 'Классическая тяга верхнего блока широким хватом',
    primary_muscles: ['широчайшие'],
    secondary_muscles: ['задние дельты', 'бицепс'],
    equipment: ['блочный тренажер'],
    tags: ['тяговое', 'базовое', 'классическое'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-12',
    name_en: 'Wide Grip Pull-ups',
    name_ru: 'Подтягивания широким прямым хватом к груди',
    description: 'Классические подтягивания широким хватом',
    primary_muscles: ['широчайшие'],
    secondary_muscles: ['задние дельты', 'бицепс'],
    equipment: ['турник'],
    tags: ['собственный вес', 'калистеника', 'классическое'],
    exercise_type: 'собственный_вес_повторения',
    difficulty_level: 'средний'
  },
  {
    id: 'mock-13',
    name_en: 'Barbell Bent Over Row',
    name_ru: 'Тяга штанги в наклоне прямым хватом',
    description: 'Классическая тяга штанги в наклоне',
    primary_muscles: ['широчайшие', 'средняя трапеция'],
    secondary_muscles: ['задние дельты', 'бицепс'],
    equipment: ['штанга'],
    tags: ['базовое', 'тяговое', 'сила', 'классическое'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'средний'
  },
  {
    id: 'mock-14',
    name_en: 'Single Arm Dumbbell Row',
    name_ru: 'Тяга гантели одной рукой на скамье',
    description: 'Тяга гантели одной рукой с опорой на скамью',
    primary_muscles: ['широчайшие', 'средняя трапеция'],
    secondary_muscles: ['задние дельты', 'бицепс'],
    equipment: ['гантели', 'скамья'],
    tags: ['базовое', 'тяговое', 'односторонние'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-15',
    name_en: 'Dumbbell Shrugs',
    name_ru: 'Шраги с гантелями',
    description: 'Классические шраги с гантелями',
    primary_muscles: ['верхняя трапеция'],
    secondary_muscles: ['средняя трапеция'],
    equipment: ['гантели'],
    tags: ['изолирующее', 'трапеции', 'классическое'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'начинающий'
  },
  // Упражнения на ноги
  {
    id: 'mock-16',
    name_en: 'Air Squats',
    name_ru: 'Воздушные приседания',
    description: 'Классические приседания с собственным весом',
    primary_muscles: ['квадрицепс', 'ягодицы'],
    secondary_muscles: ['бицепс бедра', 'икры'],
    equipment: ['собственный вес'],
    tags: ['базовое', 'ноги', 'функциональное', 'классическое'],
    exercise_type: 'собственный_вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-17',
    name_en: 'Barbell Back Squats',
    name_ru: 'Приседания со штангой',
    description: 'Классические приседания со штангой на спине',
    primary_muscles: ['квадрицепс', 'ягодицы'],
    secondary_muscles: ['бицепс бедра', 'кор'],
    equipment: ['штанга'],
    tags: ['базовое', 'ноги', 'сила', 'классическое'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'средний'
  },
  {
    id: 'mock-18',
    name_en: 'Forward Lunges',
    name_ru: 'Выпады вперёд',
    description: 'Классические выпады вперед',
    primary_muscles: ['квадрицепс', 'ягодицы'],
    secondary_muscles: ['бицепс бедра', 'икры'],
    equipment: ['собственный вес'],
    tags: ['базовое', 'ноги', 'функциональное'],
    exercise_type: 'собственный_вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-19',
    name_en: 'Leg Press Wide Stance',
    name_ru: 'Жим ногами (широкая постановка ног)',
    description: 'Жим ногами с широкой постановкой стоп',
    primary_muscles: ['квадрицепс', 'ягодицы'],
    secondary_muscles: ['приводящие', 'бицепс бедра'],
    equipment: ['тренажер'],
    tags: ['базовое', 'ноги', 'ягодицы'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-20',
    name_en: 'Standing Calf Raises',
    name_ru: 'Подъём на носки стоя на полу',
    description: 'Подъемы на носки стоя с собственным весом',
    primary_muscles: ['икры'],
    secondary_muscles: ['камбаловидная'],
    equipment: ['собственный вес'],
    tags: ['изолирующее', 'икры'],
    exercise_type: 'собственный_вес_повторения',
    difficulty_level: 'начинающий'
  },
  // Упражнения на ягодицы
  {
    id: 'mock-21',
    name_en: 'Glute Bridge',
    name_ru: 'Ягодичный мост лежа на полу',
    description: 'Классический ягодичный мост лежа на полу',
    primary_muscles: ['ягодицы'],
    secondary_muscles: ['бицепс бедра', 'кор'],
    equipment: ['собственный вес'],
    tags: ['изолирующее', 'ягодицы', 'классическое'],
    exercise_type: 'собственный_вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-22',
    name_en: 'Bulgarian Split Squats',
    name_ru: 'Болгарские приседания',
    description: 'Приседания на одной ноге с задней ногой на возвышении',
    primary_muscles: ['ягодицы', 'квадрицепс'],
    secondary_muscles: ['бицепс бедра', 'икры'],
    equipment: ['скамья'],
    tags: ['односторонние', 'ягодицы', 'функциональное'],
    exercise_type: 'собственный_вес_повторения',
    difficulty_level: 'средний'
  },
  {
    id: 'mock-23',
    name_en: 'Romanian Deadlift',
    name_ru: 'Румынская становая тяга',
    description: 'Румынская становая тяга для ягодиц и бицепса бедра',
    primary_muscles: ['ягодицы', 'бицепс бедра'],
    secondary_muscles: ['нижняя часть спины'],
    equipment: ['штанга'],
    tags: ['базовое', 'ягодицы', 'бицепс бедра'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'средний'
  },
  {
    id: 'mock-24',
    name_en: 'Cable Kickbacks',
    name_ru: 'Отведение ноги назад в блоке',
    description: 'Отведение ноги назад в кроссовере',
    primary_muscles: ['ягодицы'],
    secondary_muscles: ['бицепс бедра'],
    equipment: ['кроссовер'],
    tags: ['изолирующее', 'ягодицы'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'начинающий'
  },
  // Упражнения на плечи
  {
    id: 'mock-25',
    name_en: 'Military Press',
    name_ru: 'Армейский жим',
    description: 'Классический жим штанги стоя над головой',
    primary_muscles: ['передние дельты'],
    secondary_muscles: ['средние дельты', 'трицепс', 'кор'],
    equipment: ['штанга'],
    tags: ['базовое', 'плечи', 'сила', 'классическое'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'средний'
  },
  {
    id: 'mock-26',
    name_en: 'Seated Dumbbell Press',
    name_ru: 'Жим гантелей сидя',
    description: 'Жим гантелей над головой сидя',
    primary_muscles: ['передние дельты', 'средние дельты'],
    secondary_muscles: ['трицепс'],
    equipment: ['гантели', 'скамья'],
    tags: ['базовое', 'плечи'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-27',
    name_en: 'Seated Lateral Raise',
    name_ru: 'Махи гантелей в стороны сидя',
    description: 'Махи гантелей в стороны сидя',
    primary_muscles: ['средние дельты'],
    secondary_muscles: ['передние дельты'],
    equipment: ['гантели', 'скамья'],
    tags: ['изолирующее', 'средние дельты'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'начинающий'
  },
  {
    id: 'mock-28',
    name_en: 'Bent Over Rear Lateral Raise',
    name_ru: 'Отведения гантелей в стороны-назад в наклоне',
    description: 'Отведение гантелей назад в наклоне',
    primary_muscles: ['задние дельты'],
    secondary_muscles: ['средняя трапеция'],
    equipment: ['гантели'],
    tags: ['изолирующее', 'задние дельты'],
    exercise_type: 'вес_повторения',
    difficulty_level: 'средний'
  }
];

/**
 * Service for managing exercise catalog operations
 * Provides search, filtering, and categorization functionality
 * Handles Russian text search and display
 */
export class ExerciseCatalogService {
  /**
   * Check if running in development mode
   */
  private isDevelopmentMode(): boolean {
    return import.meta.env.VITE_DEV_MODE === 'true';
  }

  /**
   * Get all exercises from the catalog
   */
  async getAllExercises(): Promise<Exercise[]> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: using mock exercises data');
      return MOCK_EXERCISES;
    }

    const { data, error } = await supabase
      .from('exercise_catalog')
      .select('*')
      .order('name_ru');

    if (error) {
      console.error('Error fetching exercises:', error);
      throw new Error(`Failed to fetch exercises: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Search exercises with filtering and pagination
   * Supports Russian text search and array-based filtering
   * Uses the new Russian schema with primary_muscles, secondary_muscles, equipment arrays
   * Prioritizes exercises where the muscle_group is in primary_muscles
   */
  async searchExercises(options: ExerciseSearchOptions = {}): Promise<ExerciseSearchResult> {
    const {
      query = '',
      muscle_group,
      equipment,
      limit = 50,
      offset = 0
    } = options;

    if (this.isDevelopmentMode()) {
      console.log('Development mode: using mock exercises search');
      
      let filteredExercises = [...MOCK_EXERCISES];

      // Apply text search if query is provided
      if (query.trim()) {
        const normalizedQuery = query.trim().toLowerCase();
        filteredExercises = filteredExercises.filter(exercise =>
          exercise.name_en.toLowerCase().includes(normalizedQuery) ||
          exercise.name_ru.toLowerCase().includes(normalizedQuery) ||
          exercise.description?.toLowerCase().includes(normalizedQuery) ||
          exercise.primary_muscles.some(muscle => muscle.toLowerCase().includes(normalizedQuery)) ||
          exercise.secondary_muscles.some(muscle => muscle.toLowerCase().includes(normalizedQuery)) ||
          exercise.equipment.some(eq => eq.toLowerCase().includes(normalizedQuery)) ||
          exercise.tags.some(tag => tag.toLowerCase().includes(normalizedQuery))
        );
      }

      // Apply muscle group filter (check if muscle_group is in primary or secondary muscles)
      if (muscle_group) {
        // Exclude functional, cardio, and stretching exercises from basic muscle group categories
        const basicMuscleGroups = ['грудь', 'спина', 'ноги', 'ягодицы', 'плечи', 'бицепс', 'трицепс', 'предплечья'];
        const shouldExcludeSpecialCategories = basicMuscleGroups.includes(muscle_group.toLowerCase());
        
        filteredExercises = filteredExercises.filter(exercise => {
          const matchesMuscleGroup = exercise.primary_muscles.includes(muscle_group) ||
            exercise.secondary_muscles.includes(muscle_group);
          
          // If filtering by basic muscle group, exclude functional, cardio, and stretching exercises
          if (shouldExcludeSpecialCategories && matchesMuscleGroup) {
            const hasExcludedTag = exercise.tags.some(tag => 
              ['функциональное', 'функциональные', 'кардио', 'растяжка'].includes(tag.toLowerCase())
            );
            return !hasExcludedTag;
          }
          
          return matchesMuscleGroup;
        });
        
        // Sort: primary muscles first, then secondary
        filteredExercises.sort((a, b) => {
          const aIsPrimary = a.primary_muscles.includes(muscle_group);
          const bIsPrimary = b.primary_muscles.includes(muscle_group);
          
          if (aIsPrimary && !bIsPrimary) return -1;
          if (!aIsPrimary && bIsPrimary) return 1;
          return a.name_ru.localeCompare(b.name_ru);
        });
      }

      // Apply equipment filter (check if equipment is in equipment array)
      if (equipment) {
        filteredExercises = filteredExercises.filter(exercise =>
          exercise.equipment.includes(equipment)
        );
      }

      // Apply pagination
      const total_count = filteredExercises.length;
      const paginatedExercises = filteredExercises.slice(offset, offset + limit);
      const has_more = offset + limit < total_count;

      return {
        exercises: paginatedExercises,
        total_count,
        has_more
      };
    }

    try {
      // If muscle_group is specified, we need to fetch and sort manually
      // because PostgreSQL doesn't support sorting by array position easily
      if (muscle_group) {
        console.log('🔍 [searchExercises] muscle_group filter active:', muscle_group);
        
        // Fetch all matching exercises
        let queryBuilder = supabase
          .from('exercise_catalog')
          .select('*', { count: 'exact' });

        // Apply text search if query is provided
        if (query.trim()) {
          const searchTerm = query.trim();
          console.log('🔍 [searchExercises] Adding text search:', searchTerm);
          queryBuilder = queryBuilder.or(
            `name_ru.ilike.%${searchTerm}%,name_en.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`
          );
        }

        // Apply muscle group filter using contains operator
        // Note: .contains() checks if the array contains the specified value
        const filterString = `primary_muscles.cs.{${muscle_group}},secondary_muscles.cs.{${muscle_group}}`;
        console.log('🔍 [searchExercises] Adding muscle filter:', filterString);
        queryBuilder = queryBuilder.or(filterString);

        console.log('🔍 [searchExercises] Executing query...');

        // Apply equipment filter
        if (equipment) {
          console.log('🔍 [searchExercises] Adding equipment filter:', equipment);
          queryBuilder = queryBuilder.contains('equipment', [equipment]);
        }

        const { data, error } = await queryBuilder;

        console.log('📊 [searchExercises] Query result:', { 
          dataLength: data?.length || 0, 
          hasError: !!error,
          errorMessage: error?.message,
          errorDetails: error?.details,
          errorHint: error?.hint
        });

        if (error) {
          console.error('❌ [searchExercises] Error searching exercises:', error);
          throw new Error(`Failed to search exercises: ${error.message}`);
        }

        // Log first few exercises to see what we got
        if (data && data.length > 0) {
          console.log('📝 [searchExercises] First 3 exercises:', data.slice(0, 3).map(ex => ({
            name: ex.name_ru,
            primary_muscles: ex.primary_muscles,
            secondary_muscles: ex.secondary_muscles,
            tags: ex.tags
          })));
        } else {
          console.log('⚠️ [searchExercises] No exercises found!');
        }

        // Sort in JavaScript: primary muscles first
        const sortedData = (data || []).sort((a: any, b: any) => {
          const aIsPrimary = a.primary_muscles?.includes(muscle_group);
          const bIsPrimary = b.primary_muscles?.includes(muscle_group);
          
          if (aIsPrimary && !bIsPrimary) return -1;
          if (!aIsPrimary && bIsPrimary) return 1;
          return (a.name_ru || '').localeCompare(b.name_ru || '');
        });

        // Apply pagination after sorting and filtering
        const total_count = sortedData.length;
        const paginatedData = sortedData.slice(offset, offset + limit);
        const has_more = offset + limit < total_count;

        return {
          exercises: paginatedData,
          total_count,
          has_more
        };
      }

      // No muscle_group filter - use standard query
      let queryBuilder = supabase
        .from('exercise_catalog')
        .select('*', { count: 'exact' });

      // Apply text search if query is provided
      if (query.trim()) {
        const searchTerm = query.trim();
        queryBuilder = queryBuilder.or(
          `name_ru.ilike.%${searchTerm}%,name_en.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`
        );
      }

      // Apply equipment filter
      if (equipment) {
        queryBuilder = queryBuilder.contains('equipment', [equipment]);
      }

      // Apply pagination and ordering
      queryBuilder = queryBuilder
        .order('name_ru')
        .range(offset, offset + limit - 1);

      const { data, error, count } = await queryBuilder;

      if (error) {
        console.error('Error searching exercises:', error);
        throw new Error(`Failed to search exercises: ${error.message}`);
      }

      const total_count = count || 0;
      const has_more = offset + limit < total_count;

      return {
        exercises: data || [],
        total_count,
        has_more
      };
    } catch (error) {
      console.error('Search error:', error);
      throw new Error(`Search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get exercise by ID
   */
  async getExerciseById(id: string): Promise<Exercise | null> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: searching mock exercises by ID');
      return MOCK_EXERCISES.find(exercise => exercise.id === id) || null;
    }

    const { data, error } = await supabase
      .from('exercise_catalog')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null;
      }
      console.error('Error fetching exercise by ID:', error);
      throw new Error(`Failed to fetch exercise: ${error.message}`);
    }

    return data;
  }

  /**
   * Get all unique muscle groups from the catalog
   * Now works with primary_muscles array field
   */
  async getMuscleGroups(): Promise<string[]> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: getting mock muscle groups');
      const muscleGroups = new Set<string>();
      MOCK_EXERCISES.forEach(exercise => {
        exercise.primary_muscles.forEach(muscle => muscleGroups.add(muscle));
        exercise.secondary_muscles.forEach(muscle => muscleGroups.add(muscle));
      });
      return Array.from(muscleGroups).sort();
    }

    // Try to get unique muscle groups from database
    try {
      // Get all exercises and extract unique muscle groups
      const { data, error } = await supabase
        .from('exercise_catalog')
        .select('primary_muscles, secondary_muscles');

      if (error) {
        console.error('Error fetching muscle groups:', error);
        throw new Error(`Failed to fetch muscle groups: ${error.message}`);
      }

      const muscleGroups = new Set<string>();
      (data || []).forEach((exercise: any) => {
        (exercise.primary_muscles || []).forEach((muscle: string) => muscleGroups.add(muscle));
        (exercise.secondary_muscles || []).forEach((muscle: string) => muscleGroups.add(muscle));
      });

      return Array.from(muscleGroups).sort();
    } catch (error) {
      console.error('Error getting muscle groups:', error);
      return [];
    }
  }

  /**
   * Get all unique equipment types from the catalog
   * Now works with equipment array field
   */
  async getEquipmentTypes(): Promise<string[]> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: getting mock equipment types');
      const equipmentTypes = new Set<string>();
      MOCK_EXERCISES.forEach(exercise => {
        exercise.equipment.forEach(eq => equipmentTypes.add(eq));
      });
      return Array.from(equipmentTypes).sort();
    }

    // Try to use RPC function, fallback to manual query
    try {
      const { data, error } = await (supabase as any).rpc('get_unique_equipment_types');
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.warn('RPC function not available, using fallback method');
      
      // Fallback: get all exercises and extract unique equipment types
      const { data, error: fallbackError } = await supabase
        .from('exercise_catalog')
        .select('equipment');

      if (fallbackError) {
        console.error('Error fetching equipment types:', fallbackError);
        throw new Error(`Failed to fetch equipment types: ${fallbackError.message}`);
      }

      const equipmentTypes = new Set<string>();
      (data || []).forEach((exercise: any) => {
        (exercise.equipment || []).forEach((eq: string) => equipmentTypes.add(eq));
      });

      return Array.from(equipmentTypes).sort();
    }
  }

  /**
   * Get exercises by muscle group with enhanced Russian support
   * Handles both Russian muscle group names and array-based storage
   */
  async getExercisesByMuscleGroup(muscleGroup: string): Promise<Exercise[]> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: filtering mock exercises by muscle group');
      return MOCK_EXERCISES.filter(exercise => 
        exercise.primary_muscles.includes(muscleGroup) ||
        exercise.secondary_muscles.includes(muscleGroup)
      );
    }

    const { data, error } = await supabase
      .from('exercise_catalog')
      .select('*')
      .or(`primary_muscles.cs.{${muscleGroup}},secondary_muscles.cs.{${muscleGroup}}`)
      .order('name_ru');

    if (error) {
      console.error('Error fetching exercises by muscle group:', error);
      throw new Error(`Failed to fetch exercises by muscle group: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get exercises by equipment type with enhanced filtering
   * Now works with equipment array field
   */
  async getExercisesByEquipment(equipment: string): Promise<Exercise[]> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: filtering mock exercises by equipment');
      return MOCK_EXERCISES.filter(exercise => 
        exercise.equipment.includes(equipment)
      );
    }

    const { data, error } = await supabase
      .from('exercise_catalog')
      .select('*')
      .contains('equipment', [equipment])
      .order('name_ru');

    if (error) {
      console.error('Error fetching exercises by equipment:', error);
      throw new Error(`Failed to fetch exercises by equipment: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Search exercises by name (both English and Russian)
   * This is a simplified search method for quick lookups
   * Includes enhanced Russian text normalization and array field search
   */
  async searchByName(query: string): Promise<Exercise[]> {
    if (!query.trim()) {
      return [];
    }

    if (this.isDevelopmentMode()) {
      console.log('Development mode: searching mock exercises by name');
      const normalizedQuery = query.trim().toLowerCase();
      return MOCK_EXERCISES.filter(exercise =>
        exercise.name_en.toLowerCase().includes(normalizedQuery) ||
        exercise.name_ru.toLowerCase().includes(normalizedQuery)
      ).slice(0, 20); // Limit for quick search
    }

    // Try to use the search function, fallback to simple search
    try {
      const { data, error } = await (supabase as any)
        .rpc('search_exercises', { search_query: query.trim() })
        .limit(20);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.warn('RPC search function not available, using fallback');
      
      // Fallback to simple text search
      const normalizedQuery = query.trim().toLowerCase();
      const { data, error: fallbackError } = await supabase
        .from('exercise_catalog')
        .select('*')
        .or(`name_ru.ilike.%${normalizedQuery}%,name_en.ilike.%${normalizedQuery}%`)
        .order('name_ru')
        .limit(20);

      if (fallbackError) {
        console.error('Error searching exercises by name:', fallbackError);
        throw new Error(`Failed to search exercises by name: ${fallbackError.message}`);
      }

      return data || [];
    }
  }

  /**
   * Get exercise statistics (for analytics)
   */
  async getExerciseStats(): Promise<{
    total_exercises: number;
    muscle_groups: number;
    equipment_types: number;
  }> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: getting mock exercise stats');
      const muscleGroups = new Set<string>();
      const equipmentTypes = new Set<string>();
      
      MOCK_EXERCISES.forEach(exercise => {
        exercise.primary_muscles.forEach(muscle => muscleGroups.add(muscle));
        exercise.secondary_muscles.forEach(muscle => muscleGroups.add(muscle));
        exercise.equipment.forEach(eq => equipmentTypes.add(eq));
      });
      
      return {
        total_exercises: MOCK_EXERCISES.length,
        muscle_groups: muscleGroups.size,
        equipment_types: equipmentTypes.size
      };
    }

    // Get total count
    const { count: totalCount, error: countError } = await supabase
      .from('exercise_catalog')
      .select('*', { count: 'exact', head: true });

    if (countError) {
      console.error('Error getting exercise count:', countError);
      throw new Error(`Failed to get exercise count: ${countError.message}`);
    }

    // Get muscle groups and equipment types
    const [muscleGroups, equipmentTypes] = await Promise.all([
      this.getMuscleGroups(),
      this.getEquipmentTypes()
    ]);

    return {
      total_exercises: totalCount || 0,
      muscle_groups: muscleGroups.length,
      equipment_types: equipmentTypes.length
    };
  }

  /**
   * Advanced search with multiple criteria and Russian text optimization
   * Provides more sophisticated filtering and sorting options
   */
  async advancedSearch(options: {
    query?: string;
    muscle_groups?: string[];
    equipment_types?: string[];
    has_instructions?: boolean;
    sort_by?: 'name_ru' | 'name' | 'muscle_group';
    sort_order?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  } = {}): Promise<ExerciseSearchResult> {
    const {
      query = '',
      muscle_groups = [],
      equipment_types = [],
      has_instructions,
      sort_by = 'name_ru',
      sort_order = 'asc',
      limit = 50,
      offset = 0
    } = options;

    let queryBuilder = supabase
      .from('exercise_catalog')
      .select('*', { count: 'exact' });

    // Apply text search if query is provided
    if (query.trim()) {
      const normalizedQuery = query.trim().toLowerCase();
      queryBuilder = queryBuilder.or(
        `name.ilike.%${normalizedQuery}%,name_ru.ilike.%${normalizedQuery}%,instructions.ilike.%${normalizedQuery}%`
      );
    }

    // Apply muscle group filters (multiple)
    if (muscle_groups.length > 0) {
      queryBuilder = queryBuilder.in('muscle_group', muscle_groups);
    }

    // Apply equipment filters (multiple)
    if (equipment_types.length > 0) {
      queryBuilder = queryBuilder.in('equipment', equipment_types);
    }

    // Filter by instruction availability
    if (has_instructions !== undefined) {
      if (has_instructions) {
        queryBuilder = queryBuilder.not('instructions', 'is', null);
      } else {
        queryBuilder = queryBuilder.is('instructions', null);
      }
    }

    // Apply sorting
    const ascending = sort_order === 'asc';
    queryBuilder = queryBuilder.order(sort_by, { ascending });

    // Apply pagination
    queryBuilder = queryBuilder.range(offset, offset + limit - 1);

    const { data, error, count } = await queryBuilder;

    if (error) {
      console.error('Error in advanced search:', error);
      throw new Error(`Failed to perform advanced search: ${error.message}`);
    }

    const total_count = count || 0;
    const has_more = offset + limit < total_count;

    return {
      exercises: data || [],
      total_count,
      has_more
    };
  }
}

// Export a singleton instance
export const exerciseCatalogService = new ExerciseCatalogService();